import React from 'react';
import { Text } from '@uhg-abyss/web/ui/Text';

import { type IMarkdownInlineNode, parseMarkdownBlocks } from '../utilities/markdownParse.utils';

interface IMarkdownTextComponentProps {
  children: React.ReactNode;
  style?: {
    fontWeight?: '400' | '700';
    fontStyle?: 'normal' | 'italic';
    flex?: number;
  };
}

interface IMarkdownViewComponentProps {
  children?: React.ReactNode;
}

export interface IMarkdownMarkedTextStyle {
  fontWeight: '400' | '700';
  fontStyle: 'normal' | 'italic';
}

export interface IMarkdownContentRendererProps {
  content: string;
  bulletCharacter?: string;
  ContainerComponent?: React.ComponentType<IMarkdownViewComponentProps>;
  ParagraphComponent?: React.ComponentType<IMarkdownTextComponentProps>;
  ListComponent?: React.ComponentType<IMarkdownViewComponentProps>;
  ListItemComponent?: React.ComponentType<IMarkdownViewComponentProps>;
  BulletComponent?: React.ComponentType<IMarkdownTextComponentProps>;
  ListItemTextComponent?: React.ComponentType<IMarkdownTextComponentProps>;
  MarkedTextComponent?: React.ComponentType<IMarkdownTextComponentProps>;
  markedTextStyleResolver?: (isBold: boolean, isItalic: boolean) => IMarkdownMarkedTextStyle;
}

const DefaultContainer: React.FC<IMarkdownViewComponentProps> = ({ children }) => {
  return <div>{children}</div>;
};

const DefaultText: React.FC<IMarkdownTextComponentProps> = ({ children, style }) => {
  return <Text style={style}>{children}</Text>;
};

const DEFAULT_MARKED_TEXT_STYLE_RESOLVER = (
  isBold: boolean,
  isItalic: boolean,
): IMarkdownMarkedTextStyle => {
  return {
    fontWeight: isBold ? '700' : '400',
    fontStyle: isItalic ? 'italic' : 'normal',
  };
};

const renderInlineNodes = (
  nodes: IMarkdownInlineNode[],
  keyPrefix: string,
  MarkedTextComponent: React.ComponentType<IMarkdownTextComponentProps>,
  markedTextStyleResolver: (isBold: boolean, isItalic: boolean) => IMarkdownMarkedTextStyle,
): React.ReactNode[] => {
  return nodes.map((node, index) => {
    if (node.type === 'lineBreak') {
      return '\n';
    }

    if (!node.bold && !node.italic) {
      return node.content;
    }

    return (
      <MarkedTextComponent
        key={`${keyPrefix}-marked-${index}`}
        style={markedTextStyleResolver(node.bold, node.italic)}
      >
        {node.content}
      </MarkedTextComponent>
    );
  });
};

export const MarkdownContentRenderer: React.FC<IMarkdownContentRendererProps> = ({
  content,
  bulletCharacter = '•',
  ContainerComponent = DefaultContainer,
  ParagraphComponent = DefaultText,
  ListComponent = DefaultContainer,
  ListItemComponent = DefaultContainer,
  BulletComponent = DefaultText,
  ListItemTextComponent = DefaultText,
  MarkedTextComponent = DefaultText,
  markedTextStyleResolver = DEFAULT_MARKED_TEXT_STYLE_RESOLVER,
}) => {
  const blocks = parseMarkdownBlocks(content ?? '');

  if (blocks.length === 0) {
    return (
      <ContainerComponent>
        <ParagraphComponent>{content ?? ''}</ParagraphComponent>
      </ContainerComponent>
    );
  }

  const renderedBlocks: React.ReactNode[] = [];

  for (let index = 0; index < blocks.length; index += 1) {
    const block = blocks[index];

    if (block.type === 'paragraph') {
      renderedBlocks.push(
        <ParagraphComponent key={`markdown-paragraph-${index}`}>
          {renderInlineNodes(
            block.nodes,
            `markdown-paragraph-${index}`,
            MarkedTextComponent,
            markedTextStyleResolver,
          )}
        </ParagraphComponent>,
      );
      continue;
    }

    if (block.type === 'bulletList') {
      const listItems: React.ReactNode[] = [];

      for (let itemIndex = 0; itemIndex < block.items.length; itemIndex += 1) {
        listItems.push(
          <ListItemComponent key={`markdown-list-item-${index}-${itemIndex}`}>
            <BulletComponent>{bulletCharacter}</BulletComponent>
            <ListItemTextComponent style={{ flex: 1 }}>
              {renderInlineNodes(
                block.items[itemIndex],
                `markdown-list-item-${index}-${itemIndex}`,
                MarkedTextComponent,
                markedTextStyleResolver,
              )}
            </ListItemTextComponent>
          </ListItemComponent>,
        );
      }

      renderedBlocks.push(
        <ListComponent key={`markdown-list-${index}`}>{listItems}</ListComponent>,
      );
    }
  }

  return <ContainerComponent>{renderedBlocks}</ContainerComponent>;
};
